import { useState, useEffect } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Star, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyReview = { customer_name: '', location: '', rating: 5, review_text: '', trip_destination: '', photo_url: '', is_featured: false, is_active: true };

export default function AdminReviews() {
  const { token } = useAdmin();
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState(emptyReview);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    const { data } = await axios.get(`${API}/admin/reviews`, { headers });
    setReviews(data);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.customer_name || !form.review_text) return toast.error('Name and review text required');
    try {
      if (editing) {
        await axios.put(`${API}/admin/reviews/${editing}`, form, { headers });
        toast.success('Review updated');
      } else {
        await axios.post(`${API}/admin/reviews`, form, { headers });
        toast.success('Review added');
      }
      setShowForm(false); setEditing(null); setForm(emptyReview); load();
    } catch (e) { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    await axios.delete(`${API}/admin/reviews/${id}`, { headers });
    toast.success('Deleted'); load();
  };

  const edit = (r) => { setForm({ ...r }); setEditing(r.id); setShowForm(true); };
  const toggle = async (r) => {
    await axios.put(`${API}/admin/reviews/${r.id}`, { is_active: !r.is_active }, { headers });
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-reviews">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reviews & Testimonials</h1>
          <p className="text-slate-500 text-sm mt-1">Manage customer reviews shown on the website</p>
        </div>
        <Button onClick={() => { setForm(emptyReview); setEditing(null); setShowForm(true); }} data-testid="add-review-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Review
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editing ? 'Edit' : 'Add'} Review</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Customer Name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="review-name" /></div>
                <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g., Mumbai" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Rating (1-5)</Label><Input type="number" min={1} max={5} step={0.5} value={form.rating} onChange={(e) => setForm({ ...form, rating: parseFloat(e.target.value) })} data-testid="review-rating" /></div>
                <div><Label>Trip Destination</Label><Input value={form.trip_destination} onChange={(e) => setForm({ ...form, trip_destination: e.target.value })} placeholder="e.g., Bali" /></div>
              </div>
              <div><Label>Review Text</Label><Textarea value={form.review_text} onChange={(e) => setForm({ ...form, review_text: e.target.value })} rows={4} data-testid="review-text" /></div>
              <div><Label>Photo URL (optional)</Label><Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://..." /></div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2"><Switch checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} /><Label>Featured</Label></div>
                <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /><Label>Active</Label></div>
              </div>
              <Button className="w-full" onClick={save} data-testid="save-review-btn">{editing ? 'Update' : 'Create'} Review</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* List */}
      <div className="grid md:grid-cols-2 gap-4">
        {reviews.length === 0 && <p className="text-slate-500 text-center py-8 col-span-2">No reviews yet. Add customer testimonials!</p>}
        {reviews.map((r) => (
          <Card key={r.id} className={`${!r.is_active ? 'opacity-60' : ''}`} data-testid={`review-card-${r.id}`}>
            <CardContent className="py-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {r.photo_url ? (
                    <img src={r.photo_url} alt={r.customer_name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold">
                      {r.customer_name?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-slate-800">{r.customer_name}</p>
                    <p className="text-xs text-slate-500">{r.location} {r.trip_destination && `• ${r.trip_destination}`}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={`star-${i}`} className={`w-3 h-3 ${i < Math.floor(r.rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                  ))}
                </div>
              </div>
              <p className="text-sm text-slate-600 mt-3 line-clamp-3">{r.review_text}</p>
              <div className="flex items-center justify-between mt-3 pt-3 border-t">
                <div className="flex items-center gap-2">
                  {r.is_featured && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Featured</span>}
                </div>
                <div className="flex items-center gap-1">
                  <Switch checked={r.is_active} onCheckedChange={() => toggle(r)} />
                  <Button variant="ghost" size="icon" onClick={() => edit(r)}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(r.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
